import depsloader from "../../../depsloader";

const REQUEST_TIMEOUT = 20000;

function isFiniteNumber(value) {
  return typeof value === "number" && isFinite(value);
}

function nonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function mapsRuntime() {
  if (typeof window === "undefined" || !window.ScreenworkMapsRuntime) {
    return {};
  }
  return window.ScreenworkMapsRuntime;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestSeq = 0;
  }

  // Laufzeitkonfiguration. Der Key kommt serverseitig aus der Maps-Konfiguration
  // und wird nie im Frontend zusammengebaut.
  _config() {
    const runtime = mapsRuntime();

    return {
      searchUrl: nonEmptyString(runtime.geocoderUrl) || "https://api.screenwork.de/geocoder/search",
      reverseUrl: nonEmptyString(runtime.geocoderReverseUrl) || "https://api.screenwork.de/geocoder/reverse",
      key: typeof runtime.geocoderKey === "string" ? runtime.geocoderKey.trim() : ""
    };
  }

  // Invalidiert laufende Anfragen: verspaetete Antworten duerfen Treffer und
  // Ladezustand danach nicht mehr veraendern.
  cancelAutocomplete() {
    this.requestSeq++;

    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel("cancelled");
      this.axiosRequest = null;
    }
  }

  destroy() {
    this.cancelAutocomplete();
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    // Ohne Orts- und Strassenangabe bleibt der Feature-Name die Beschriftung.
    // Er wird bewusst nicht als Stadt ausgegeben.
    if (p.length === 0 && g.name) {
      return g.name;
    }

    return p.join(", ");
  }

  // Feature -> Geoadresse. properties.type und district haben Vorrang;
  // osm_key/osm_value greifen nur noch als Legacy-Fallback.
  _geoaddress(l) {
    if (!l || typeof l !== "object") {
      return null;
    }

    const props = l.properties && typeof l.properties === "object" ? l.properties : null;
    const geometry = l.geometry && typeof l.geometry === "object" ? l.geometry : null;
    if (!props || !geometry || !Array.isArray(geometry.coordinates)) {
      return null;
    }

    // Reihenfolge ist [lng, lat].
    const lng = Number(geometry.coordinates[0]);
    const lat = Number(geometry.coordinates[1]);
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const type = nonEmptyString(props.type);
    const name = nonEmptyString(props.name);
    const geoaddress = {};

    // Strasse
    let street = nonEmptyString(props.street);
    const number = nonEmptyString(props.housenumber);
    if (!street) {
      if (type === "street") {
        street = name;
      } else if (!type && props.osm_key === "highway") {
        street = name; // Legacy-Fallback
      }
    }
    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    // Postleitzahl
    const zip = nonEmptyString(props.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }

    // Stadtteil
    let district = nonEmptyString(props.district);
    if (!district) {
      if (type === "district") {
        district = name;
      } else if (!type) {
        // Legacy-Fallback des alten Adapters
        const legacyDistrict =
          props.osm_key === "boundary" ||
          (props.osm_key === "place" && props.osm_value === "suburb");
        const legacyCity = nonEmptyString(props.city);
        if (legacyDistrict && name && legacyCity && name !== legacyCity) {
          district = name;
        }
      }
    }

    // Stadt
    let city = nonEmptyString(props.city);
    if (!city) {
      if (type === "city" || type === "locality") {
        city = name;
      } else if (!type) {
        city = name; // Legacy-Fallback
      }
    }

    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }
    if (name) {
      geoaddress["name"] = name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    const state = nonEmptyString(props.state);
    const country = nonEmptyString(props.country);
    if (state) {
      geoaddress["state"] = state;
    }
    if (country) {
      geoaddress["country"] = country;
    }

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    const cfg = this._config();
    if (!cfg.key) {
      return Promise.resolve([]);
    }

    const CancelToken = this.axios.CancelToken;
    const source = CancelToken.source();
    const seq = ++this.requestSeq;
    this.axiosRequest = { source: source, seq: seq };

    let timer = setTimeout(() => {
      source.cancel("timeout");
    }, REQUEST_TIMEOUT);

    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      // Ein altes finally darf eine neuere Suche nicht zuruecksetzen.
      if (this.axiosRequest && this.axiosRequest.seq === seq) {
        this.axiosRequest = null;
      }
    };

    return this.axios
      .get(cfg.searchUrl, { params: params, cancelToken: source.token })
      .then(res => {
        clear();
        if (seq !== this.requestSeq) {
          return []; // veraltete Antwort
        }

        const data = res && res.data;
        const features = data && Array.isArray(data.features) ? data.features : [];
        const results = [];

        features.forEach(feature => {
          const geoaddress = this._geoaddress(feature);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });

        return results;
      })
      .catch(thrown => {
        clear();
        if (this.axios.isCancel(thrown) || seq !== this.requestSeq) {
          return []; // Abbruch/Timeout enden kontrolliert
        }
        throw thrown;
      });
  }

  autocomplete(q) {
    const cfg = this._config();
    const query = typeof q === "string" ? q.trim() : "";

    // Laufende Anfrage immer beenden, auch bei leerer Eingabe.
    this.cancelAutocomplete();

    if (!cfg.key || query === "") {
      return Promise.resolve([]);
    }

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");
    params.append("key", cfg.key);

    return this.geocode(params);
  }

  // Liefert null statt einer Rejection, wenn kein Ergebnis moeglich ist.
  // Die Aufrufer pruefen auf ein Ergebnis und fangen keine Fehler ab.
  reverse(lat, lng) {
    const cfg = this._config();
    const nlat = Number(lat);
    const nlng = Number(lng);

    if (!cfg.key) {
      return Promise.resolve(null);
    }
    if (!isFiniteNumber(nlat) || !isFiniteNumber(nlng)) {
      return Promise.resolve(null);
    }
    if (nlat < -90 || nlat > 90 || nlng < -180 || nlng > 180) {
      return Promise.resolve(null);
    }

    const source = this.axios.CancelToken.source();
    let timer = setTimeout(() => {
      source.cancel("timeout");
    }, REQUEST_TIMEOUT);

    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const params = new URLSearchParams();
    params.append("lat", String(nlat));
    // Der Parameter heisst lon, nicht lng.
    params.append("lon", String(nlng));
    params.append("lang", "de");
    params.append("key", cfg.key);

    return this.axios
      .get(cfg.reverseUrl, { params: params, cancelToken: source.token })
      .then(res => {
        clear();
        const data = res && res.data;
        const features = data && Array.isArray(data.features) ? data.features : [];
        if (features.length === 0) {
          return null;
        }
        return this._geoaddress(features[0]);
      })
      .catch(thrown => {
        clear();
        if (this.axios.isCancel(thrown)) {
          return null;
        }
        throw thrown;
      });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
